import { z } from 'zod';
import { APP_SETTING_KEY } from './app-settings-keys';

export const ThemePreferenceSchema = z.enum(['system', 'light', 'dark']);

export type ThemePreference = z.infer<typeof ThemePreferenceSchema>;

export const APP_SETTINGS_SCHEMA = {
  [APP_SETTING_KEY.TERMS_ACCEPTED_VERSIONS]: z.array(z.string()),
  [APP_SETTING_KEY.THEME_PREFERENCE]: ThemePreferenceSchema,
} as const;

export type AppSettingKey = keyof typeof APP_SETTINGS_SCHEMA;

export type AppSettings = {
  [K in AppSettingKey]: z.infer<(typeof APP_SETTINGS_SCHEMA)[K]>;
};

export type AppSettingValue = AppSettings[AppSettingKey];

export type AppSettingChange = {
  key: AppSettingKey;
  value: AppSettingValue;
};

export const isAppSettingKey = (key: unknown): key is AppSettingKey =>
  typeof key === 'string' &&
  Object.prototype.hasOwnProperty.call(APP_SETTINGS_SCHEMA, key);

export const APP_SETTING_KEYS: AppSettingKey[] =
  Object.keys(APP_SETTINGS_SCHEMA).filter(isAppSettingKey);

export const parseAppSetting = <K extends AppSettingKey>({
  key,
  value,
}: {
  key: K;
  value: unknown;
}): AppSettings[K] | null => {
  const parsed = APP_SETTINGS_SCHEMA[key].safeParse(value);

  return parsed.success ? (parsed.data as AppSettings[K]) : null;
};
